import { faHouse } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import { ServiceSiteKind, ServiceType } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  EmptyState,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
} from '../../components/ui';
import { MY_SERVICE_SITES } from '../../services/serviceService/queries';
import {
  useDeactivateServiceSiteMutation,
  useMyServiceSitesQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { authStore } from '../../../store/authStore';
import { modalActions } from '../../../store/authStore/modalStore';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { SiteFormModal } from './SiteFormModal';
import { defaultServiceSearch, searchPath } from './serviceSearchState';
import { siteSummary, type ServiceSite } from './serviceSiteForm';

const Grid = styled.div`
  ${tw`grid grid-cols-1 gap-5 sm:grid-cols-2`}
`;

/**
 * The customer's homes and vehicles (spec 019 W14). A site with an open job
 * cannot be turned off; the server says so (`SITE_IN_USE`).
 */
export default function ServiceSitesPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useSnapshot(authStore);
  const query = useMyServiceSitesQuery({
    variables: { includeInactive: true },
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });
  const [deactivate] = useDeactivateServiceSiteMutation({
    refetchQueries: [
      { query: MY_SERVICE_SITES },
      { query: MY_SERVICE_SITES, variables: { includeInactive: true } },
    ],
  });
  const [editing, setEditing] = useState<ServiceSite | null | ServiceSiteKind>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const sites = query.data?.myServiceSites ?? [];

  if (!isAuthenticated) {
    return (
      <PageContainer width="md">
        <Alert tone="info" title="Sign in to add your homes and vehicles">
          <Button
            type="button"
            size="sm"
            text="Sign in"
            onClick={() => modalActions.openSignInModal(() => undefined)}
          />
        </Alert>
      </PageContainer>
    );
  }

  const onDeactivate = async (site: ServiceSite) => {
    if (!window.confirm(`Turn off ${site.nickname}? You can add it again later.`)) return;
    setBusy(site.id);
    try {
      await deactivate({ variables: { siteId: site.id } });
      toast.success(`${site.nickname} turned off`);
    } catch (e) {
      toast.error(stayErrorMessage(e, 'Could not turn it off'), { autoClose: 8000 });
    } finally {
      setBusy(null);
    }
  };

  const findWorker = (site: ServiceSite) =>
    navigate(
      searchPath({
        ...defaultServiceSearch(),
        siteId: site.id,
        where: site.nickname,
        zip: site.zip,
        serviceType:
          site.kind === ServiceSiteKind.Home
            ? ServiceType.HomeTurnover
            : ServiceType.VehicleCleaning,
      }),
    );

  return (
    <PageContainer width="lg" canvas data-testid="service-sites">
      <PageHeader
        title="Homes and vehicles"
        description="Where jobs happen. Workers see the address and how to get in only for their own jobs."
        actions={
          <InlineRow>
            <Button
              type="button"
              theme="secondary"
              text="Add a vehicle"
              onClick={() => setEditing(ServiceSiteKind.Vehicle)}
            />
            <Button
              type="button"
              text="Add a home"
              onClick={() => setEditing(ServiceSiteKind.Home)}
              dataTestId="sites-add-home"
            />
          </InlineRow>
        }
      />
      {query.loading && !query.data ? (
        <LoadingBlock label="Loading" />
      ) : sites.length === 0 ? (
        <Card>
          <EmptyState
            icon={faHouse}
            title="Nothing here yet"
            description="Add the rental home or the car you want cleaned or handed over."
            action={
              <Button
                type="button"
                text="Add a home"
                onClick={() => setEditing(ServiceSiteKind.Home)}
              />
            }
          />
        </Card>
      ) : (
        <Grid>
          {sites.map(site => (
            <Card key={site.id} data-testid={`site-card-${site.id}`}>
              <CardBody>
                <Stack $gap={4}>
                  <div>
                    <InlineRow>
                      <Strong>{site.nickname}</Strong>
                      <Badge tone="neutral" size="sm">
                        {site.kind === ServiceSiteKind.Home ? 'Home' : 'Vehicle'}
                      </Badge>
                      {!site.isActive && (
                        <Badge tone="warning" size="sm">
                          Off
                        </Badge>
                      )}
                    </InlineRow>
                    <div>
                      <Muted $size="sm">
                        {site.addressLine1}, {site.city}, {site.state} {site.zip}
                      </Muted>
                    </div>
                    <div>
                      <Muted $size="sm">
                        {siteSummary(site)}
                        {site.hasAccessInstructions ? ' · Entry set' : ' · No entry details yet'}
                      </Muted>
                    </div>
                  </div>
                  <InlineRow style={{ flexWrap: 'wrap' }}>
                    {site.isActive && (
                      <Button
                        type="button"
                        size="sm"
                        text="Find a worker"
                        onClick={() => findWorker(site)}
                      />
                    )}
                    <Button
                      type="button"
                      theme="secondary"
                      size="sm"
                      text="Edit"
                      onClick={() => setEditing(site)}
                    />
                    {site.isActive && (
                      <Button
                        type="button"
                        theme="text"
                        size="sm"
                        text="Turn off"
                        isLoading={busy === site.id}
                        onClick={() => onDeactivate(site)}
                      />
                    )}
                  </InlineRow>
                </Stack>
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}
      {editing !== null && (
        <SiteFormModal
          site={typeof editing === 'object' ? editing : null}
          kind={typeof editing === 'string' ? editing : undefined}
          onClose={() => setEditing(null)}
        />
      )}
    </PageContainer>
  );
}
